<template>
    <div class="pane-content">
        <el-row type="flex" class="align-center mt-2">
            <h5 class="section-header">Sync Settings</h5>
        </el-row>
        <salesforce-contact-settings
                ref="salesforceContactSettings"
                :model="model"
                :statics="staticName"
                v-if="integrationName === SALESFORCE_INTEGRATION"
        />

        <el-row type="flex" class="mt-2 align-center"
                v-if="CRMModuleWasChanged">
            <el-alert
                    class="mb-3"
                    type="warning"
                    :closable="false"
                    show-icon>
                <div class="d-block">
                    <div class="mb-2 text-black">
                        <template>
                            Please save the CRM module selection to enable all functionality.
                        </template>
                    </div>
                </div>
            </el-alert>
        </el-row>
        <div class="flex-column align-center"
             v-if="integrationName === HUBSPOT_INTEGRATION">
            <hubspot-sync-contact-mapper
                    :model="model"
                    :static-name="staticName"
                    :integration-name-with-casing="integrationNameWithCasing"
                    :default-mapper-settings="defaultMapperSettings"
                    class="mt-4"
            />
        </div>

        <!-- End Synchronization -->
        <!-- Start Push and Pull Contacts -->
        <el-row type="flex" class="mt-35 align-center"
                v-if="pushContactsIsAvailable || pullContactsIsAvailable">
            <h5 class="section-header">One-time Operations</h5>
        </el-row>
        <el-row v-if="pushContactsIsAvailable || pullContactsIsAvailable">
            <span v-if="integrationName !== SALESFORCE_INTEGRATION">
                {{ staticName }} automatically stays in sync with {{ integrationNameWithCasing }}, but in certain cases, you may need to do a one-time push and pull operation.
            </span>
            <span v-else>
              {{ staticName }} automatically stays in sync with {{ integrationNameWithCasing }}, but this feature is intended for emergency situations only.
          </span>
        </el-row>
        <el-row type="flex" :gutter="20" align="middle" class="mt-4"
                v-if="pushContactsIsAvailable || pullContactsIsAvailable">
            <el-col :span="15">
                <div class="flex-column">
                    <el-row v-if="pullContactsIsAvailable">
                        <b class="section-subheader">One-time Operations: {{ currentModulePluralAndFirstCapitalize }}</b>
                    </el-row>
                    <div class="flex-column">
                        <span v-if="pushContactsIsAvailable">
                            Use "Push Contacts" to see your {{ staticName }} contacts in {{ integrationNameWithCasing }}.
                        </span>
                        <span v-if="pullContactsIsAvailable">
                            Use "Pull {{ currentModulePluralAndFirstCapitalize }}" to see all your {{ integrationNameWithCasing }} contacts in {{ staticName }}.
                        </span>
                        <div class="sync-warning-note"
                             v-if="pullContactsIsAvailable">
                            <i class="el-icon-warning-outline mr-1"></i>
                            Pulling contacts will cause LRN lookup charges to be applied.
                        </div>
                    </div>
                </div>
            </el-col>
            <el-col :span="10">
                <div class="flex-column justify-center h-100">
                    <div class="flex-row gap-5">
                        <el-button
                                @click="pullContacts"
                                :disabled="isPullingContacts || hubspotDisablePushAndPullContacts"
                                :loading="isPullingContacts"
                                v-if="pullContactsIsAvailable"
                                size="mini"
                                icon="el-icon-bottom"
                        >
                            Pull {{ currentModulePluralAndFirstCapitalize }}
                        </el-button>
                        <el-button
                                @click="pushContacts"
                                :disabled="isPushingContacts || hubspotDisablePushAndPullContacts"
                                :loading="isPushingContacts"
                                v-if="pushContactsIsAvailable"
                                size="mini"
                                icon="el-icon-top"
                        >
                            Push Contacts
                        </el-button>
                    </div>
                    <small v-if="lastSyncPullContact" class="mt-1 sync-time-text">
                        Last pull {{ currentModulePluralAndFirstCapitalize }}: <b>{{ lastSyncPullContact | fixFullDateUTCRelative }}</b>
                    </small>
                    <small v-if="pushContactsIsAvailable && model.settings?.last_sync_push_contact"
                           class="sync-time-text">
                        Last push contact: <b>{{ model.settings.last_sync_push_contact | fixFullDateUTCRelative }}</b>
                    </small>
                </div>
            </el-col>
        </el-row>
        <!-- End Push and Pull Contacts -->
        <!-- Start Pull Lead Statuses -->
        <el-row type="flex" :gutter="20" align="middle" class="mt-35"
                v-if="pullLeadStatusesIsAvailable">
            <el-col :span="15">
                <div class="flex-column">
                    <el-row type="flex" class="mb-1 align-center">
                        <b class="section-subheader">Pull Lead Statuses</b>
                    </el-row>
                    <div class="flex-column">
                        <span>
                            Pull your {{ integrationNameWithCasing }} lead statuses to {{ staticName }} as disposition statuses.
                        </span>
                    </div>
                </div>
            </el-col>
            <el-col :span="10">
                <div class="flex-column justify-center h-100">
                    <div>
                        <el-button
                                @click="pullLeadStatuses"
                                :disabled="isPullingLeadStatuses"
                                :loading="isPullingLeadStatuses"
                                size="mini"
                                icon="el-icon-bottom"
                        >
                            Pull Lead Statuses
                        </el-button>
                    </div>
                    <small v-if="model.settings?.last_sync_disposition_status" class="mt-2 sync-time-text">
                        Last disposition status sync:
                        <b>{{ model.settings.last_sync_disposition_status | fixFullDateUTCRelative }}</b>
                    </small>
                </div>
            </el-col>
        </el-row>
        <!-- End Pull Lead Statuses -->
        <!-- Start Pull Call Outcomes -->
        <el-row type="flex" :gutter="20" align="middle" class="mt-35"
                v-if="pullOutcomesIsAvailable">
            <el-col :span="15">
                <div class="flex-column">
                    <el-row type="flex" class="mb-1 align-center">
                        <b class="section-subheader">Pull Call Outcomes</b>
                    </el-row>
                    <div class="flex-column">
                        <span>
                            Pull your {{ integrationNameWithCasing }} call outcomes to {{ staticName }} as call disposition statuses.
                        </span>
                    </div>
                </div>
            </el-col>
            <el-col :span="10">
                <div class="flex-column justify-center h-100">
                    <div>
                        <el-button
                                @click="pullCallOutcomes"
                                :disabled="isPullingCallOutcomes"
                                :loading="isPullingCallOutcomes"
                                size="mini"
                                icon="el-icon-bottom"
                        >
                            Pull Call Outcomes
                        </el-button>
                    </div>
                    <small v-if="model.settings?.last_sync_call_disposition" class="mt-2 sync-time-text">
                        Last disposition status sync:
                        <b>{{ model.settings.last_sync_call_disposition | fixFullDateUTCRelative }}</b>
                    </small>
                </div>
            </el-col>
        </el-row>
        <!-- End Pull Call Outcomes -->
        <!-- Start Pull Activity Types -->
        <el-row type="flex" :gutter="20" align="middle" class="mt-35"
                v-if="pullActivityTypesIsAvailable">
            <el-col :span="15">
                <div class="flex-column">
                    <el-row type="flex" class="mb-1 align-center">
                        <b class="section-subheader">Pull Activity Types</b>
                    </el-row>
                    <div class="flex-column">
                        <span>
                            Pull your {{ integrationNameWithCasing }} activity types to {{ staticName }} as activity types.
                        </span>
                    </div>
                </div>
            </el-col>
            <el-col :span="10">
                <div class="flex-column justify-center h-100">
                    <div>
                        <el-button
                                @click="pullActivityTypes"
                                :disabled="isPullingActivityTypes"
                                :loading="isPullingActivityTypes"
                                size="mini"
                                icon="el-icon-bottom"
                        >
                            Pull Activity Types
                        </el-button>
                    </div>
                    <small v-if="model.settings?.last_sync_call_activity_types" class="mt-2 sync-time-text">
                        Last activity types sync:
                        <b>{{ model.settings.last_sync_call_activity_types | fixFullDateUTCRelative }}</b>
                    </small>
                </div>
            </el-col>
        </el-row>
        <!-- End Pull Activity Types -->
        <!-- Start Pull Deals -->
        <el-row type="flex" :gutter="20" align="middle" class="mt-35"
                v-if="pullDealsIsAvailable">
            <el-col :span="15">
                <div class="flex-column">
                    <el-row type="flex" class="mb-1 align-center">
                        <b class="section-subheader">Pull Deals</b>
                    </el-row>
                    <div class="flex-column">
                        <span>
                            Pull your {{ integrationNameWithCasing }} Deals to {{ staticName }}.
                        </span>
                    </div>
                </div>
            </el-col>
            <el-col :span="10">
                <div class="flex-column justify-center h-100">
                    <div>
                        <el-button
                                @click="pullDeals"
                                :disabled="isPullingDeals"
                                :loading="isPullingDeals"
                                size="mini"
                                icon="el-icon-bottom"
                        >
                            Pull Deals
                        </el-button>
                    </div>
                    <small v-if="model.settings?.last_sync_deals" class="mt-2 sync-time-text">
                        Last deals sync: <b>{{ model.settings.last_sync_deals | fixFullDateUTCRelative }}</b>
                    </small>
                </div>
            </el-col>
        </el-row>
        <!-- End Pull Deals -->
        <!-- Start Sync Users -->
        <el-row type="flex" :gutter="20" align="middle" class="mt-35"
                v-if="integrationName === SALESFORCE_INTEGRATION">
          <el-col :span="15">
            <div class="flex-column">
              <el-row type="flex" class="mb-1 align-center">
                <b class="section-subheader">Sync Users</b>
                <el-tooltip
                  effect="dark"
                  placement="top">
                  <div slot="content">
                    Heads up! Users are synced by email, please make sure your agents have
                    <br/>
                    the same email address on {{ staticName }} and {{ integrationNameWithCasing }}.
                  </div>
                  <i class="el-icon-info ml-1"></i>
                </el-tooltip>
              </el-row>
              <div class="flex-column">
                          <span>
                              Connect your {{ integrationNameWithCasing }} to {{ staticName }} users.
                          </span>
                <span>
                              Only current users in {{ staticName }} are synced, and no new users will be created.
                          </span>
              </div>
            </div>
          </el-col>
          <el-col :span="10">
            <div class="flex-column justify-center h-100">
              <div>
                <el-button
                  @click="pullUsers"
                  :disabled="isPullingUsers"
                  :loading="isPullingUsers"
                  size="mini"
                  icon="el-icon-refresh"
                >
                  Sync Users
                </el-button>
              </div>
              <small v-if="model.settings?.last_sync_user" class="mt-2 sync-time-text">
                Last user sync: <b>{{ model.settings.last_sync_user | fixFullDateUTCRelative }}</b>
              </small>
            </div>
          </el-col>
        </el-row>
        <!-- End Sync Users -->
    </div>
</template>
<script>
import {mapGetters, mapState} from 'vuex'
import HubspotSyncUsersMapper from './hubspot/sync-user-mapper.vue'
import HubspotSyncContactMapper from './hubspot/sync-contact-mapper.vue'
import {HUBSPOT_INTEGRATION, SALESFORCE_INTEGRATION} from "../../../constants/integrations"
import {MODULE_ALL, MODULE_CONTACT, MODULE_LEAD} from "../../../constants/integration_modules"
import SalesforceContactSettings from "./salesforce/salesforce-contact-settings.vue"

export default {
    components: {
        SalesforceContactSettings,
        HubspotSyncUsersMapper,
        HubspotSyncContactMapper,
    },
    props: {
        model: {
            type: Object,
            required: true,
        },
        integrationName: {
            type: String,
            required: true,
        },
        integrationNameWithCasing: {
            type: String,
            required: true
        },
        statics: {
            type: Object,
            required: true,
        },
        defaultMapperSettings: {
            type: Object,
            required: true,
        }
    },
    data: () => ({
        isPullingContacts: false,
        isPushingContacts: false,
        isPullingLeadStatuses: false,
        isPullingCallOutcomes: false,
        isPullingActivityTypes: false,
        isPullingUsers: false,
        isPullingDeals: false,
        HUBSPOT_INTEGRATION,
        SALESFORCE_INTEGRATION,
        module: MODULE_CONTACT,
    }),
    computed: {
        ...mapState({
            currentCompany: state => state.cache.current_company
        }),
        ...mapGetters(['CRMModuleWasChanged']),
        hasFeatureVersion2() {
            return this.model?.credentials?.feature_version >= 2
        },
        staticName() {
            return this.statics.name || 'Aloware'
        },
        hubspotDisablePushAndPullContacts() {
            return [585, 1000].includes(this.currentCompany.id) && this.integrationName === HUBSPOT_INTEGRATION
        },
        pushContactsIsAvailable() {
            return this.integrationName === HUBSPOT_INTEGRATION
        },
        pullContactsIsAvailable() {
            switch (this.integrationName) {
                case HUBSPOT_INTEGRATION:
                    return true
                case SALESFORCE_INTEGRATION:
                    return !this.CRMModuleWasChanged
                default:
                    return false
            }
        },
        pullLeadStatusesIsAvailable() {
            switch (this.integrationName) {
              case HUBSPOT_INTEGRATION:
                return true
              case SALESFORCE_INTEGRATION:
                return !this.CRMModuleWasChanged && this.model.settings?.contact_settings?.enable_lead
              default:
                return false
            }
        },
        pullOutcomesIsAvailable() {
            return this.integrationName === HUBSPOT_INTEGRATION
        },
        pullActivityTypesIsAvailable() {
            return this.integrationName === HUBSPOT_INTEGRATION
        },
        pullDealsIsAvailable() {
            return this.integrationName === HUBSPOT_INTEGRATION
        },
        currentModule() {
            if (this.integrationName === SALESFORCE_INTEGRATION) {
                if (this.model.settings?.contact_settings?.enable_contact && this.model.settings?.contact_settings?.enable_lead) {
                    return MODULE_ALL
                } else if (this.model.settings?.contact_settings?.enable_lead) {
                    return MODULE_LEAD
                }
            }

            return MODULE_CONTACT
        },
        allModulesEnabled() {
            return this.integrationName === SALESFORCE_INTEGRATION
                    && this.hasFeatureVersion2
                    && this.model.settings?.contact_settings?.enable_lead
                    && this.model.settings?.contact_settings?.enable_contact
        },
        currentModulePluralAndFirstCapitalize() {
            if (this.allModulesEnabled) {
                return this.model.settings?.contact_settings?.priority === MODULE_LEAD ? 'Leads / Contacts' : 'Contacts / Leads'
            } else {
                return this.currentModule.charAt(0).toUpperCase() + this.currentModule.slice(1)
            }
        },
        lastSyncPullContact() {
            switch (this.integrationName) {
                case SALESFORCE_INTEGRATION:
                    switch (this.currentModule) {
                        case MODULE_CONTACT:
                            return this.model.settings?.last_sync_contact
                        case MODULE_ALL:
                            return this.model.settings?.last_sync_all
                        case MODULE_LEAD:
                        default:
                            return this.model.settings?.last_sync_lead
                    }
                default:
                    return this.model.settings?.last_sync_pull_contact
            }
        }
    },
    methods: {
        async pullContacts() {
            this.isPullingContacts = true
            try {
                await axios.post('/api/v1/integration/' + this.integrationName + '/pull-contacts')
                this.$notify({
                    offset: 95,
                    title: this.integrationNameWithCasing,
                    message: this.currentModulePluralAndFirstCapitalize + ' are being pulled from ' + this.integrationNameWithCasing + ', please wait for a few minutes for this process to finish. You will receive an email when we finish processing your request.',
                    type: 'success'
                })
            } catch (err) {
                this.$root.handleErrors(err.response)
            } finally {
                this.isPullingContacts = false
            }
        },
        async pushContacts() {
            this.isPushingContacts = true

            try {
                await axios.post('/api/v1/integration/' + this.integrationName + '/push-contacts')
                this.$notify({
                    offset: 95,
                    title: this.integrationNameWithCasing,
                    message: 'Contacts are being pushed to ' + this.integrationNameWithCasing + ', please wait for a few minutes for this process to finish. You will receive an email when we finish processing your request.',
                    type: 'success'
                })
            } catch (err) {
                this.$root.handleErrors(err.response)
            } finally {
                this.isPushingContacts = false
            }
        },
        async pullUsers() {
            this.isPullingUsers = true
            await new Promise(resolve => setTimeout(resolve, 1000))

            try {
                await axios.post('/api/v1/integration/' + this.integrationName + '/pull-users')
                this.$notify({
                    offset: 95,
                    title: this.integrationNameWithCasing,
                    message: 'Users are being synced with ' + this.integrationNameWithCasing + ', please wait for a few minutes for this process to finish. You will receive an email when we finish processing your request.',
                    type: 'success'
                })
            } catch (err) {
                this.$root.handleErrors(err.response)
            } finally {
                this.isPullingUsers = false
            }
        },
        async pullLeadStatuses() {
            this.isPullingLeadStatuses = true

            try {
                await axios.post('/api/v1/integration/' + this.integrationName + '/pull-lead-statuses')
                this.$notify({
                    offset: 95,
                    title: this.integrationNameWithCasing,
                    message: 'Lead status are being pulled from ' + this.integrationNameWithCasing + ', please wait for a few minutes for this process to finish. You will receive an email when we finish processing your request.',
                    type: 'success'
                })
            } catch (err) {
                this.$root.handleErrors(err.response)
            } finally {
                this.isPullingLeadStatuses = false
            }
        },
        async pullCallOutcomes() {
            this.isPullingCallOutcomes = true

            try {
                await axios.post('/api/v1/integration/' + this.integrationName + '/pull-call-dispositions')
                this.$notify({
                    offset: 95,
                    title: this.integrationNameWithCasing,
                    message: 'Call dispositions are being pulled from ' + this.integrationNameWithCasing + ', please wait for a few minutes for this process to finish. You will receive an email when we finish processing your request.',
                    type: 'success'
                })
            } catch (err) {
                this.$root.handleErrors(err.response)
            } finally {
                this.isPullingCallOutcomes = false
            }
        },
        async pullActivityTypes() {
            this.isPullingActivityTypes = true

            try {
                await axios.post('/api/v1/integration/' + this.integrationName + '/pull-activity-types')
                this.$notify({
                    offset: 95,
                    title: this.integrationNameWithCasing,
                    message: 'Activity types are being pulled from ' + this.integrationNameWithCasing + ', please wait for a few minutes for this process to finish. You will receive an email when we finish processing your request.',
                    type: 'success'
                })
            } catch (err) {
                this.$root.handleErrors(err.response)
            } finally {
                this.isPullingActivityTypes = false
            }
        },
        async pullDeals() {
            this.isPullingDeals = true

            try {
                await axios.post('/api/v1/integration/' + this.integrationName + '/pull-deals')
                this.$notify({
                    offset: 95,
                    title: this.integrationNameWithCasing,
                    message: 'Deals are being pulled from ' + this.integrationNameWithCasing + ', please wait for a few minutes for this process to finish. You will receive an email when we finish processing your request.',
                    type: 'success'
                })
            } catch (err) {
                this.$root.handleErrors(err.response)
            } finally {
                this.isPullingDeals = false
            }
        }
    },
}
</script>

<style scoped>
.pane-content {
    display: flex;
    flex-direction: column;
    padding-left: 8px;
    padding-right: 8px;
}

.section-header {
    margin: 0 12px 0 0;
    font-size: 20px;
}

.section-subheader {
    font-size: 16px;
}

.align-center {
    align-items: center;
}

.flex-column {
    display: flex;
    flex-direction: column;
}

.flex-row {
    display: flex;
    flex-direction: row;
}

.justify-center {
    justify-content: center;
}

.h-100 {
    height: 100%;
}

.gap-5 {
    gap: 5px;
}

.mt-35 {
    margin-top: 35px;
}

.sync-time-text {
    font-size: 12px;
    color: #606266;
}

.sync-warning-note {
    display: inline-flex;
    align-items: center;
    background: #fffbe6;
    border: 1px solid #ffe58f;
    border-radius: 5px;
    padding: 10px 16px;
    color: #614700;
    margin-top: 5px;
    font-size: 14px;
    width: auto;
    max-width: max-content;
}
</style>
